import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Stack,
  CircularProgress,
  Alert,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
  Paper,
  Fade,
  MenuItem,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useNotify, useUpdate, useRefresh, useGetMany, useGetIdentity } from 'react-admin';
import { Funeral } from '../types';
import { labelWarnings } from './labelSafety';
import { useShipDayPlan } from './shipDayApi';
import { getToken } from '../authentication/authProvider';
import { buildTrackingMessage } from './trackingMessage';
import {
  DEFAULT_LABEL_NOTIFY_CHOICE,
  LABEL_NOTIFY_OPTIONS,
  LabelNotifyChoice,
  LabelNotifyResult,
  SHIPPING_NOTIFY_RESET,
  createLabelButtonText,
  describeNotifyOutcome,
} from './labelNotify';

interface ShippingRate {
  serviceCode: string;
  serviceName: string;
  price: number;
  basePrice: number;
  deliveryDate?: string;
  deliveryDays?: number;
  guaranteedDelivery: boolean;
}

/** Canada Post rejects postal codes that aren't uppercase and unspaced (e.g. "L1N0l1"). */
const normalizePostalCode = (postalCode?: string | null): string =>
  (postalCode || '').replace(/\s/g, '').toUpperCase();

/**
 * Where the remains ship from, as the server decides it (T-192): the sender on
 * the label and the rate origin. Montréal for Québec, "Cleo Cremation" at
 * Brampton VC or Ajax for Ontario; Milton doesn't ship until a site is picked.
 */
interface ShipsFromInfo {
  ok: boolean;
  site?: string;
  reason?: string;
  needsSite?: boolean;
  sender?: { senderName: string; senderAddress: string; senderCity: string; senderProvince: string; senderPostalCode: string };
  sites: { id: string; name: string }[];
}

interface ShippingLabelModalProps {
  open: boolean;
  onClose: () => void;
  funeral: Funeral;
}

export const ShippingLabelModal: React.FC<ShippingLabelModalProps> = ({ 
  open, 
  onClose,
  funeral 
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isLoading: isUpdating }] = useUpdate();
  
  // Box size and weight
  const [weight, setWeight] = useState('2');
  const [boxLength, setBoxLength] = useState('25');
  const [boxWidth, setBoxWidth] = useState('25');
  const [boxHeight, setBoxHeight] = useState('25');
  
  // Rates and selection
  const [rates, setRates] = useState<ShippingRate[]>([]);
  const [selectedRate, setSelectedRate] = useState<string>('');
  const [loadingRates, setLoadingRates] = useState(false);
  const [ratesLoaded, setRatesLoaded] = useState(false);
  const [creatingShipment, setCreatingShipment] = useState(false);

  // Step 1 prices the label and asks how the family should be told; step 2
  // shows the tracking number and what the family was told. The choice is made
  // BEFORE the label exists and applied by the server once it does (T-114), so
  // there is no unsent draft to lose and no second button that sends.
  const [step, setStep] = useState<'rates' | 'done'>('rates');
  const [label, setLabel] = useState<{ trackingPin: string; labelUrl: string } | null>(null);
  const [notifyChoice, setNotifyChoice] = useState<LabelNotifyChoice>(DEFAULT_LABEL_NOTIFY_CHOICE);
  const [notifyOutcome, setNotifyOutcome] = useState<LabelNotifyResult | null>(null);
  // The two steps ride side by side on one track that slides left — forward
  // progress, the way a wizard should read. Both stay mounted, so the track
  // needs an explicit height: the panels are different lengths and an
  // unmanaged container would jump to the taller one.
  const stepOneRef = useRef<HTMLDivElement>(null);
  const stepTwoRef = useRef<HTMLDivElement>(null);
  const [trackHeight, setTrackHeight] = useState<number | undefined>(undefined);

  // aria-hidden hides a panel from assistive tech but leaves its controls in
  // the tab order, so tabbing out of the message box walked into the rate
  // form sitting off-screen — and focusing it scrolls the overflow container,
  // shifting the slid track out of alignment. `inert` is the thing that
  // actually removes a subtree from focus; set on the element because the JSX
  // attribute needs React 19.
  useLayoutEffect(() => {
    const inactive = step === 'rates' ? stepTwoRef.current : stepOneRef.current;
    const active = step === 'rates' ? stepOneRef.current : stepTwoRef.current;
    if (inactive) (inactive as any).inert = true;
    if (active) (active as any).inert = false;
  }, [step]);

  useLayoutEffect(() => {
    const el = step === 'rates' ? stepOneRef.current : stepTwoRef.current;
    if (!el) return;
    const apply = () => setTrackHeight(el.offsetHeight);
    apply();
    // Rates arrive after a fetch and the textarea grows as staff edit it, so
    // the height has to follow the panel rather than be measured once.
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => observer.disconnect();
  }, [step, open, ratesLoaded, loadingRates]);

  const { identity } = useGetIdentity();
  // One fetch for both names: who we're texting, and whose ashes these are.
  const { data: people } = useGetMany(
    'clients',
    { ids: [funeral.primary_client_id, funeral.deceased_client_id].filter(Boolean) },
    { enabled: open }
  );
  const primaryClient = people?.find((c: any) => c.id === funeral.primary_client_id);
  const deceasedClient = people?.find((c: any) => c.id === funeral.deceased_client_id);

  const delivery = funeral.delivery_address;

  const [shipsFrom, setShipsFrom] = useState<ShipsFromInfo | null>(null);
  const [savingSite, setSavingSite] = useState(false);
  const loadShipsFrom = async (): Promise<ShipsFromInfo | null> => {
    try {
      const apiUrl = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = await getToken();
      const r = await fetch(`${baseUrl}/canada-post/funeral/${funeral.id}/ships-from`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const info = r.ok ? ((await r.json()) as ShipsFromInfo) : null;
      setShipsFrom(info);
      return info;
    } catch {
      setShipsFrom(null);
      return null;
    }
  };

  // Milton remains go out from Brampton or Ajax: staff pick the site here and
  // it is saved on the case, so the label and any later one use it.
  const handlePickSite = (siteId: string) => {
    setSavingSite(true);
    void update(
      'funerals',
      { id: funeral.id, data: { ships_from_partner_id: siteId }, previousData: funeral },
      {
        onSuccess: () => {
          void loadShipsFrom().then((info) => {
            setSavingSite(false);
            setRatesLoaded(false);
            if (info?.ok) void handleGetRates();
          });
        },
        onError: (error) => {
          setSavingSite(false);
          notify(`Could not set the ships-from site: ${error instanceof Error ? error.message : 'Unknown error'}`, {
            type: 'error',
          });
        },
      },
    );
  };

  // T-116 label safety: the one warning is a case with no ship day — its label
  // files under no day on the deliveries list (case 851). Staff can set the day
  // right here (prefilled with the server's ship day for the shipping site), or
  // click a second time ("Create label anyway") to go ahead without one.
  // Pickup dates and other crematoria are never checked.
  const [savedShipDay, setSavedShipDay] = useState<string | null>(null);
  const [shipDayInput, setShipDayInput] = useState('');
  const [savingShipDay, setSavingShipDay] = useState(false);
  const warnings = labelWarnings({
    deliveryDay: savedShipDay ?? funeral.delivery_day,
    deliveryMethod: funeral.delivery_method,
  });
  const [confirmingWarnings, setConfirmingWarnings] = useState(false);
  // The prefill is the server's ship day (ShipDayService, T-204): the next
  // pickup from the site this ships from, the same day the Slack card shows.
  const shipDayPlan = useShipDayPlan(funeral.id, open && !funeral.delivery_day);
  useEffect(() => {
    if (!open || funeral.delivery_day || shipDayInput) return;
    if (shipDayPlan?.day) setShipDayInput(shipDayPlan.day);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shipDayPlan]);

  const handleSetShipDay = () => {
    if (!shipDayInput) return;
    setSavingShipDay(true);
    void update(
      'funerals',
      { id: funeral.id, data: { delivery_day: shipDayInput }, previousData: funeral },
      {
        onSuccess: () => {
          setSavedShipDay(shipDayInput);
          setConfirmingWarnings(false);
          setSavingShipDay(false);
          notify('Ship day set', { type: 'success' });
        },
        onError: (error) => {
          setSavingShipDay(false);
          notify(`Could not set the ship day: ${error instanceof Error ? error.message : 'Unknown error'}`, {
            type: 'error',
          });
        },
      },
    );
  };

  const formatAddress = () => {
    if (!delivery) return 'No delivery address set';
    const parts = [];
    
    // Format street (apartment-civic address)
    const streetParts = [];
    if (delivery.apartment && delivery.civic_number) {
      streetParts.push(`${delivery.apartment}-${delivery.civic_number}`);
    } else if (delivery.apartment) {
      streetParts.push(delivery.apartment);
    } else if (delivery.civic_number) {
      streetParts.push(delivery.civic_number);
    }
    if (delivery.address) {
      streetParts.push(delivery.address);
    }
    if (streetParts.length > 0) {
      parts.push(streetParts.join(' '));
    }
    
    if (delivery.city) parts.push(delivery.city);
    if (delivery.province) parts.push(delivery.province);
    if (delivery.postal_code) parts.push(delivery.postal_code);
    return parts.join(', ');
  };

  const handleGetRates = async () => {
    if (!delivery?.postal_code) {
      notify('Destination postal code is required', { type: 'error' });
      return;
    }

    try {
      setLoadingRates(true);
      
      const apiUrl = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = await getToken();

      const response = await fetch(`${baseUrl}/canada-post/rates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          // The server prices from where this case ships (T-192).
          funeralId: funeral.id,
          destinationPostalCode: normalizePostalCode(delivery.postal_code),
          weight: parseFloat(weight),
          length: parseFloat(boxLength),
          width: parseFloat(boxWidth),
          height: parseFloat(boxHeight),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch rates');
      }

      const data = await response.json();
      // Filter to only show Xpresspost options
      const xpresspostRates = (data.rates || []).filter(
        (rate: ShippingRate) => rate.serviceName.toLowerCase().includes('xpresspost')
      );
      setRates(xpresspostRates);
      setRatesLoaded(true);

      // Auto-select first (cheapest) Xpresspost option
      if (xpresspostRates.length > 0) {
        setSelectedRate(xpresspostRates[0].serviceCode);
      }
    } catch (error) {
      console.error('Error fetching rates:', error);
      notify(`Error fetching rates: ${error instanceof Error ? error.message : 'Unknown error'}`, { type: 'error' });
    } finally {
      setLoadingRates(false);
    }
  };

  const handleCreateShipment = async () => {
    if (!selectedRate || !delivery) {
      notify('Please select a shipping option', { type: 'error' });
      return;
    }

    try {
      setCreatingShipment(true);
      
      const apiUrl = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = await getToken();

      // Format street address for Canada Post (line 1 = street, line 2 = apartment)
      const streetParts = [];
      if (delivery.civic_number) {
        streetParts.push(delivery.civic_number);
      }
      if (delivery.address) {
        streetParts.push(delivery.address);
      }
      const recipientAddress = streetParts.join(' ');
      const recipientAddressLine2 = delivery.apartment || undefined;

      const response = await fetch(`${baseUrl}/canada-post/shipment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          serviceCode: selectedRate,
          // Sender, return address and shipping point are set by the server
          // from where this case ships (T-192).
          funeralId: funeral.id,
          // Recipient info
          recipientName: delivery.recipient_name || 'Recipient',
          recipientAddress,
          recipientAddressLine2,
          recipientCity: delivery.city,
          recipientProvince: delivery.province,
          recipientPostalCode: normalizePostalCode(delivery.postal_code),
          recipientPhone: delivery.recipient_phone || '438-817-1770', // Use recipient phone or fallback to sender
          // Box size and weight
          weight: parseFloat(weight),
          length: parseFloat(boxLength),
          width: parseFloat(boxWidth),
          height: parseFloat(boxHeight),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create shipment');
      }

      const data = await response.json();

      // Save the label on the case. The notification fields are cleared with
      // the new tracking number, so nothing queued or sent for an earlier
      // label carries over to this one (T-114).
      let saved = true;
      try {
        await update(
          'funerals',
          {
            id: funeral.id,
            data: {
              shipping_label_url: data.labelUrl,
              shipping_tracking_number: data.trackingPin,
              shipping_shipment_id: data.shipmentId,
              delivery_method: 'shipping',
              ...SHIPPING_NOTIFY_RESET,
            },
            previousData: funeral,
          },
          { returnPromise: true }
        );
      } catch (error: any) {
        // The label is bought and Canada Post has the shipment; only our own
        // row failed to save. Carry on to step 2 regardless: the tracking
        // number exists nowhere else, and dropping it here means it's gone and
        // the next click buys a second label.
        saved = false;
        notify(
          `Label created (tracking ${data.trackingPin}) but saving it to the case failed: ${error?.message}. Copy the tracking number before closing.`,
          { type: 'warning', autoHideDuration: 15000 }
        );
      }

      // Record the label and apply the notification choice on the server. One
      // call, whatever the choice: it queues, sends the text and email
      // together, or records that the family was not told, with a feed line
      // naming who chose.
      let outcome: LabelNotifyResult | null = null;
      if (saved) {
        outcome = await fetch(`${baseUrl}/canada-post/funeral/${funeral.id}/label-created`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            trackingNumber: data.trackingPin,
            notify: notifyChoice,
            staffName: identity?.fullName,
          }),
        })
          .then((r) => (r.ok ? r.json() : null))
          .then((r: { notify?: { result?: LabelNotifyResult } } | null) => r?.notify?.result ?? null)
          .catch(() => null);
      }

      setLabel({ trackingPin: data.trackingPin, labelUrl: data.labelUrl });
      setNotifyOutcome(outcome);
      setStep('done');
      void refresh();
    } catch (error) {
      console.error('Error creating shipment:', error);
      notify(`Error creating shipment: ${error instanceof Error ? error.message : 'Unknown error'}`, { type: 'error' });
    } finally {
      setCreatingShipment(false);
    }
  };

  const resetAndClose = () => {
    onClose();
    // Reset for the next case — the dialog instance is reused.
    setNotifyChoice(DEFAULT_LABEL_NOTIFY_CHOICE);
    setNotifyOutcome(null);
    setStep('rates');
    setLabel(null);
    setRatesLoaded(false);
    setRates([]);
    setSelectedRate('');
    setConfirmingWarnings(false);
    setSavedShipDay(null);
    setShipDayInput('');
    setShipsFrom(null);
  };

  // Fetch rates when modal opens
  useEffect(() => {
    if (open && !ratesLoaded) {
      void handleGetRates();
    }
    if (open) void loadShipsFrom();
  }, [open]);

  const cheapestRate = rates.length > 0 ? rates[0] : null;

  return (
    <Dialog 
      open={open}
      // While the label is being bought, a stray click or Escape must not
      // close the dialog and lose the tracking number. The buttons still close.
      onClose={() => {
        if (creatingShipment) return;
        resetAndClose();
      }}
      maxWidth="md"
      fullWidth
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>
        <Fade key={step} in timeout={320}>
          <span>
            {step === 'rates' ? 'Create Canada Post Shipping Label' : 'Label created'}
          </span>
        </Fade>
      </DialogTitle>

      {/* overflowX hidden so the sliding panel never bounces the dialog wider */}
      <DialogContent sx={{ overflowX: 'hidden' }}>
        <Box
          sx={{
            overflow: 'hidden',
            height: trackHeight,
            transition: 'height 340ms cubic-bezier(0.4, 0, 0.2, 1)',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              width: '200%',
              transform: step === 'rates' ? 'translateX(0)' : 'translateX(-50%)',
              transition: 'transform 380ms cubic-bezier(0.4, 0, 0.2, 1)',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          >
            {/* Step 1 — rates. Stays mounted off-screen left; hidden from the
                keyboard so Tab can't wander into it. */}
            <Box
              ref={stepOneRef}
              aria-hidden={step !== 'rates'}
              sx={{
                flex: '0 0 50%',
                width: '50%',
                pointerEvents: step === 'rates' ? 'auto' : 'none',
              }}
            >
        <Stack spacing={3} sx={{ mt: 2 }}>
          {warnings.length > 0 && (
            <Alert severity="warning" data-testid="label-warning">
              {warnings.map((warning) => (
                <div key={warning.kind} data-testid={`label-warning-${warning.kind}`}>
                  {warning.message}
                </div>
              ))}
              {shipDayPlan?.missing && (
                <div data-testid="ship-day-missing">{shipDayPlan.missing}. Set them on the partner, or pick a day here.</div>
              )}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                <TextField
                  type="date"
                  size="small"
                  label="Ship day"
                  value={shipDayInput}
                  onChange={(e) => setShipDayInput(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ bgcolor: 'background.paper' }}
                  inputProps={{ 'data-testid': 'ship-day-input' }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={handleSetShipDay}
                  disabled={!shipDayInput || savingShipDay}
                >
                  {savingShipDay ? 'Saving…' : 'Set ship day'}
                </Button>
              </Stack>
            </Alert>
          )}
          {/* Delivery Address */}
          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight="600">
              Delivery Address
            </Typography>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              {delivery?.recipient_name && (
                <Typography variant="body2" fontWeight="600">
                  {delivery.recipient_name}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {formatAddress()}
              </Typography>
            </Box>
          </Box>

          {/* Ships from (T-192) */}
          {shipsFrom && (
            <Box data-testid="ships-from">
              <Typography variant="subtitle1" gutterBottom fontWeight="600">
                Ships From
              </Typography>
              {shipsFrom.ok && shipsFrom.sender ? (
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight="600">
                    {shipsFrom.sender.senderName} · {shipsFrom.site}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[shipsFrom.sender.senderAddress, shipsFrom.sender.senderCity, shipsFrom.sender.senderProvince, shipsFrom.sender.senderPostalCode].join(', ')}
                  </Typography>
                </Box>
              ) : (
                <Alert severity="warning">
                  <div>Can't create this label: {shipsFrom.reason}.</div>
                  {shipsFrom.needsSite && shipsFrom.sites.length > 0 && (
                    <TextField
                      select
                      size="small"
                      label="Ships from"
                      value=""
                      disabled={savingSite}
                      onChange={(e) => handlePickSite(e.target.value)}
                      sx={{ mt: 1.5, minWidth: 260, bgcolor: 'background.paper' }}
                    >
                      {shipsFrom.sites.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Alert>
              )}
            </Box>
          )}

          <Divider />

          {/* Box size and weight */}
          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight="600">
              Box Details
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Weight (kg)"
                type="number"
                value={weight}
                onChange={(e) => {
                  setWeight(e.target.value);
                  setRatesLoaded(false); // Reset rates when the box details change
                }}
                fullWidth
                size="small"
                inputProps={{ min: 0, step: 0.1 }}
              />
              
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Length (cm)"
                  type="number"
                  value={boxLength}
                  onChange={(e) => {
                    setBoxLength(e.target.value);
                    setRatesLoaded(false);
                  }}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0 }}
                />
                <TextField
                  label="Width (cm)"
                  type="number"
                  value={boxWidth}
                  onChange={(e) => {
                    setBoxWidth(e.target.value);
                    setRatesLoaded(false);
                  }}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0 }}
                />
                <TextField
                  label="Height (cm)"
                  type="number"
                  value={boxHeight}
                  onChange={(e) => {
                    setBoxHeight(e.target.value);
                    setRatesLoaded(false);
                  }}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0 }}
                />
              </Stack>
              
              {!ratesLoaded && (
                <Button 
                  variant="outlined" 
                  onClick={handleGetRates}
                  disabled={loadingRates}
                  fullWidth
                >
                  {loadingRates ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Fetching Rates...
                    </>
                  ) : (
                    'Get Shipping Rates'
                  )}
                </Button>
              )}
            </Stack>
          </Box>

          {/* Shipping Options */}
          {ratesLoaded && rates.length > 0 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="600">
                Select Shipping Option
              </Typography>
              
              <RadioGroup value={selectedRate} onChange={(e) => setSelectedRate(e.target.value)}>
                <Stack spacing={2}>
                  {rates.map((rate, index) => {
                    const isCheapest = index === 0;
                    const savings = isCheapest && rates.length > 1 
                      ? Math.round(((rates[1].price - rate.price) / rates[1].price) * 100)
                      : 0;

                    return (
                      <Paper
                        key={rate.serviceCode}
                        elevation={selectedRate === rate.serviceCode ? 3 : 1}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          border: selectedRate === rate.serviceCode ? '2px solid' : '1px solid',
                          borderColor: selectedRate === rate.serviceCode ? 'primary.main' : 'grey.300',
                          '&:hover': {
                            borderColor: 'primary.main',
                          },
                        }}
                        onClick={() => setSelectedRate(rate.serviceCode)}
                      >
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Radio
                            checked={selectedRate === rate.serviceCode}
                            value={rate.serviceCode}
                            sx={{ p: 0 }}
                          />
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                            <LocalShippingIcon sx={{ color: '#d93025', fontSize: 40, mr: 2 }} />
                            <Box sx={{ flex: 1 }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                <Typography variant="body1" fontWeight="600">
                                  {rate.serviceName}
                                </Typography>
                                {isCheapest && (
                                  <>
                                    <Chip 
                                      label="Cheapest" 
                                      size="small" 
                                      sx={{ 
                                        bgcolor: '#d4edda', 
                                        color: '#155724',
                                        height: '20px',
                                        fontSize: '11px'
                                      }} 
                                    />
                                    {savings > 0 && (
                                      <Chip 
                                        label={`Save ${savings}%`} 
                                        size="small" 
                                        sx={{ 
                                          bgcolor: '#cfe2ff', 
                                          color: '#084298',
                                          height: '20px',
                                          fontSize: '11px'
                                        }} 
                                      />
                                    )}
                                  </>
                                )}
                              </Stack>
                              
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    Tracking & delivery confirmation
                                  </Typography>
                                </Stack>
                              </Stack>
                              
                              {rate.deliveryDays && (
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                                  <LocalShippingIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    Delivery time: {rate.deliveryDays} {rate.deliveryDays === 1 ? 'day' : 'days'}
                                  </Typography>
                                </Stack>
                              )}
                            </Box>
                          </Box>
                          
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="h6" fontWeight="700">
                              ${rate.price.toFixed(2)} CAD
                            </Typography>
                            {rate.basePrice > rate.price && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  textDecoration: 'line-through',
                                  color: 'text.secondary' 
                                }}
                              >
                                ${rate.basePrice.toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </RadioGroup>
            </Box>
          )}

          {/* How the family is told (T-114). One explicit choice, applied by
              the server once the label exists; the primary button repeats it. */}
          {ratesLoaded && rates.length > 0 && (
            <Box data-testid="label-notify-choice">
              <Typography variant="subtitle1" gutterBottom fontWeight="600" id="label-notify-heading">
                How should the family be told?
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                {primaryClient
                  ? `${(primaryClient as any).name} · ${
                      [
                        (primaryClient as any).mobile_phone_number || (primaryClient as any).phone_number
                          ? 'text'
                          : null,
                        (primaryClient as any).email ? 'email' : null,
                      ]
                        .filter(Boolean)
                        .join(' + ') || 'no phone or email on file'
                    } · ${funeral.language === 'fr' ? 'French' : 'English'}`
                  : 'No primary contact on this case, so there is nobody to notify.'}
              </Typography>
              <RadioGroup
                aria-labelledby="label-notify-heading"
                value={notifyChoice}
                onChange={(e) => setNotifyChoice(e.target.value as LabelNotifyChoice)}
              >
                <Stack spacing={1}>
                  {LABEL_NOTIFY_OPTIONS.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio size="small" />}
                      sx={{ alignItems: 'flex-start', m: 0 }}
                      label={
                        <Box sx={{ pt: 0.75 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {option.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.helper}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </Stack>
              </RadioGroup>
              {notifyChoice !== 'none' && (
                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                    The text (the email says the same, with the expected delivery day)
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {buildTrackingMessage(funeral.language === 'fr' ? 'fr' : 'en', {
                      trackingPin: '[tracking number]',
                      recipientFirstName: (primaryClient as any)?.first_name,
                      deceasedFirstName: (deceasedClient as any)?.first_name,
                      urnTrackingNumber: funeral.urn_tracking_number,
                      // The link encodes the placeholder; show it readable.
                    }).replace(encodeURIComponent('[tracking number]'), '[tracking number]')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {ratesLoaded && rates.length === 0 && (
            <Alert severity="warning">
              No shipping rates available for this destination.
            </Alert>
          )}

          {/* Delivery Note */}
          {funeral.delivery_note && (
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="600">
                Delivery Note
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {funeral.delivery_note}
                </Typography>
              </Box>
            </Box>
          )}
        </Stack>
            </Box>

            {/* Step 2 — the label exists. Waits off-screen right until the
                track slides. */}
            <Box
              ref={stepTwoRef}
              aria-hidden={step !== 'done'}
              sx={{
                flex: '0 0 50%',
                width: '50%',
                pointerEvents: step === 'done' ? 'auto' : 'none',
              }}
            >
            <Stack spacing={3} sx={{ mt: 2 }}>
              <Alert severity="success" icon={<CheckCircleIcon fontSize="inherit" />}>
                <Typography variant="body2">
                  Tracking number{' '}
                  <Box component="strong" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {label?.trackingPin}
                  </Box>
                </Typography>
              </Alert>

              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LocalShippingIcon />}
                  onClick={() => label?.labelUrl && window.open(label.labelUrl, '_blank')}
                  disabled={!label?.labelUrl}
                >
                  Print label
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    if (!label?.trackingPin) return;
                    void navigator.clipboard?.writeText(label.trackingPin);
                    notify('Tracking number copied', { type: 'info' });
                  }}
                >
                  Copy tracking number
                </Button>
              </Stack>

              {(() => {
                const outcome = describeNotifyOutcome(notifyOutcome);
                return (
                  <Alert severity={outcome.severity} data-testid="label-notify-outcome">
                    <Typography variant="body2">{outcome.text}</Typography>
                  </Alert>
                );
              })()}
            </Stack>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        {step === 'rates' ? (
          <>
            <Button onClick={resetAndClose} disabled={loadingRates || creatingShipment}>
              Cancel
            </Button>

            {ratesLoaded && (
              <Button
                variant="contained"
                color={confirmingWarnings ? 'warning' : 'primary'}
                onClick={() => {
                  if (warnings.length > 0 && !confirmingWarnings) {
                    setConfirmingWarnings(true);
                    return;
                  }
                  setConfirmingWarnings(false);
                  void handleCreateShipment();
                }}
                disabled={!selectedRate || creatingShipment || isUpdating}
              >
                {creatingShipment || isUpdating ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Creating Label...
                  </>
                ) : confirmingWarnings ? (
                  'Create label anyway'
                ) : (
                  createLabelButtonText(notifyChoice)
                )}
              </Button>
            )}
          </>
        ) : (
          <Button variant="contained" onClick={resetAndClose}>
            Done
          </Button>
        )}
      </DialogActions>

    </Dialog>
  );
};
